import React, { useState } from "react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, Container, CssBaseline, Stack, useTheme } from "@mui/material";
import Logo from "@src/images/igLogo/Logo_Lockup_Horiz.png";

const isValidShopId = (value: string): boolean => {
  return /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/.test(value.trim());
};

const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getStoreName = (shopId: string): string => {
  return shopId.replace(".myshopify.com", "").replace(/-/g, " ");
};

export const InstallPage = () => {
  const theme = useTheme();
  const searchParams = new URLSearchParams(window.location.search);

  const shopIdFromUrl = searchParams.get("shopId");
  const salesRep = searchParams.get("utm_content");

  const [shopIdInput, setShopIdInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleInstall = (shopId: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set("shop", shopId.trim());
    params.delete("shopId");
    // Redirect directly to backend install-launch endpoint (bypasses HMAC validation)
    const baseUrl = import.meta.env.VITE_BASE_URL || "";
    window.location.href = `${baseUrl}/v2/install-launch?${params.toString()}`;
  };

  const handleContinue = () => {
    const trimmed = shopIdInput.trim();
    if (!trimmed) {
      setError("Please enter your Shopify store URL");
      return;
    }
    if (!isValidShopId(trimmed)) {
      setError("Please enter a valid .myshopify.com URL");
      return;
    }
    setError(null);
    handleInstall(trimmed);
  };

  const hasShopId = shopIdFromUrl && isValidShopId(shopIdFromUrl);

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        py: 4,
      }}
    >
      <CssBaseline />

      <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
        <img
          src={Logo}
          alt="Intelligems"
          style={{ maxWidth: 200, height: "auto" }}
        />

        {salesRep && (
          <IgTypography
            variant="body1"
            sx={{ color: theme.palette.text.secondary, textAlign: "center" }}
          >
            {capitalize(salesRep)} from Intelligems shared this link with you
          </IgTypography>
        )}

        {hasShopId ? (
          <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
            <IgTypography variant="h2" sx={{ textAlign: "center" }}>
              You're almost there!
            </IgTypography>

            <IgTypography
              variant="body1"
              sx={{ color: theme.palette.text.secondary, textAlign: "center" }}
            >
              Click below to install Intelligems on your store
            </IgTypography>

            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                px: 3,
                py: 2,
                width: "100%",
                textAlign: "center",
              }}
            >
              <IgTypography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
              >
                Installing on{" "}
              </IgTypography>
              <IgTypography
                variant="body2"
                fontWeight={"bold"}
                sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
              >
                {shopIdFromUrl}
              </IgTypography>
            </Box>

            <IgButton
              fullWidth
              onClick={() => handleInstall(shopIdFromUrl)}
              sx={{ minHeight: 48 }}
            >
              Install Intelligems
            </IgButton>
          </Stack>
        ) : (
          <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
            <IgTypography variant="h2" sx={{ textAlign: "center" }}>
              Get Started with Intelligems
            </IgTypography>

            <IgTypography
              variant="body1"
              sx={{ color: theme.palette.text.secondary, textAlign: "center" }}
            >
              Enter your Shopify store URL to continue
            </IgTypography>

            <IgTextField
              fullWidth
              label="Shopify Store URL"
              placeholder="your-store.myshopify.com"
              value={shopIdInput}
              onChange={(e) => {
                setShopIdInput(e.target.value);
                if (error) {
                  setError(null);
                }
              }}
              error={!!error}
              helperText={error}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleContinue();
                }
              }}
            />

            <IgButton fullWidth onClick={handleContinue} sx={{ minHeight: 48 }}>
              Continue
            </IgButton>
          </Stack>
        )}
      </Stack>
    </Container>
  );
};
